<script setup lang="ts">
import { ref, reactive } from 'vue';
import { Mail, MessageCircle, Phone, Send, CheckCircle2 } from 'lucide-vue-next';
import { sendWhatsApp } from '@torim-app/utils/lib/contact';
import { validateInputPhone } from '@torim-app/utils/lib/validations';
import { useToast } from 'vue-toastification';
import { LandingSiteApi } from '@app/api/landing-site/landing-site-api';
import { trackEvent } from '@app/composables/posthog';

const toast = useToast();

const formData = reactive({
  fullName: '',
  phone: '',
  email: '',
  message: ''
});

const errors = reactive({
  fullName: false,
  phone: false,
  message: false
});

const isLoading = ref(false);
const isSent = ref(false);

const validateForm = (): boolean => {
  errors.fullName = !formData.fullName || formData.fullName.length < 2;
  errors.phone = !validateInputPhone(formData.phone);
  errors.message = !formData.message || formData.message.length < 2;
  return !errors.fullName && !errors.phone && !errors.message;
};

const resetIsSent = () => {
  isSent.value = false;
};

const handleFormSubmit = async (): Promise<void> => {
  if (isLoading.value || isSent.value) return;
  if (!validateForm()) { trackEvent('landing_lead_validation_failed'); return; }
  trackEvent('landing_lead_submit_attempted');
  isLoading.value = true;
  try {
    const messageText = formData.email
      ? `מייל: ${formData.email}\n${formData.message}`
      : formData.message;

    await LandingSiteApi.sendContactUsEmail({
      fullName: formData.fullName,
      phone: formData.phone,
      businessType: '',
      message: messageText
    });

    isSent.value = true;
    trackEvent('landing_lead_submitted');
    trackEvent('contact_form_submitted', { status: 'success' });
    try { window.fbq?.('track', 'Contact'); } catch {}
    toast.success('ההודעה נשלחה בהצלחה! תודה שיצרתם קשר');
  } catch (e) {
    trackEvent('landing_lead_failed');
    toast.error('לא הצלחנו לשלוח את פנייתכם, אנא נסו שוב');
  }
  isLoading.value = false;
};
</script>

<template>
  <section id="contact" class="py-16 lg:py-24 bg-white relative overflow-hidden">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
      <div class="bg-slate-900 rounded-[2.5rem] overflow-hidden shadow-2xl flex flex-col lg:flex-row">

        <!-- Right Side: Contact Info -->
        <div
          class="bg-gradient-to-br from-pink-600 to-purple-700 text-white p-8 lg:p-12 lg:w-2/5 flex flex-col justify-between relative overflow-hidden">
          <div
            class="absolute top-0 left-0 w-64 h-64 rounded-full -translate-x-1/2 -translate-y-1/2" style="background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)"></div>
          <div class="relative z-10">
            <h3 class="text-3xl font-black mb-4">יש לכם שאלות?<br />אנחנו כאן בשבילכם.</h3>
            <p class="text-pink-100 mb-10 text-sm sm:text-base leading-relaxed">
              הצוות שלנו זמין לעזור לכם בכל שאלה, התלבטות טכנית או בקשה. השאירו פרטים ונחזור אליכם בהקדם האפשרי.
            </p>

            <div class="space-y-6 mb-12">
              <div class="flex items-center gap-4">
                <div class="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center shrink-0">
                  <Phone class="w-5 h-5" />
                </div>
                <div>
                  <p class="text-pink-100 text-xs mb-1">ווטסאפ (מענה בימים א'-ה')</p>
                  <a href="#" @click.prevent="sendWhatsApp('0559299380', 'היי, הגעתי מהאתר ויש לי שאלה'); trackEvent('contact_phone_clicked')"
                     class="font-bold text-lg hover:underline !text-white" dir="ltr">055-929-9380</a>
                </div>
              </div>
              <div class="flex items-center gap-4">
                <div class="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center shrink-0">
                  <Mail class="w-5 h-5" />
                </div>
                <div>
                  <p class="text-pink-100 text-xs mb-1">אימייל</p>
                  <a target="_blank" href="mailto:app@time-it.co.il" @click="trackEvent('contact_email_clicked')" class="font-bold text-lg hover:underline font-sans !text-white">app@time-it.co.il</a>
                </div>
              </div>
            </div>
          </div>

          <div class="relative z-10">
            <button
              @click="sendWhatsApp('0559299380', 'היי, הגעתי מהאתר ויש לי שאלה'); trackEvent('contact_whatsapp_clicked')"
              class="w-full bg-white text-green-500 hover:bg-slate-50 font-bold py-4 rounded-xl flex items-center justify-center gap-2 transition-colors shadow-lg">
              <MessageCircle class="w-5 h-5 text-green-500 shrink-0" />
              <span>שלחו לנו הודעה בוואטסאפ</span>
            </button>
          </div>
        </div>

        <!-- Left Side: Contact Form / Success State -->
        <div class="p-8 lg:p-12 lg:w-3/5 flex flex-col">

          <!-- Success State -->
          <div v-if="isSent" class="flex flex-col items-center justify-center flex-1 text-center py-8">
            <div class="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mb-4">
              <CheckCircle2 class="w-8 h-8 text-green-400" />
            </div>
            <h4 class="text-2xl font-bold text-white mb-2">ההודעה נשלחה!</h4>
            <p class="text-slate-400 mb-8">תודה שיצרתם קשר. הצוות שלנו יחזור אליכם בהקדם.</p>
            <button
              @click="resetIsSent"
              class="bg-slate-700 hover:bg-slate-600 text-white font-medium px-6 py-3 rounded-xl transition-colors">
              שליחת הודעה נוספת
            </button>
          </div>

          <!-- Form -->
          <template v-else>
            <h4 class="text-2xl font-bold text-white mb-6">השאירו פרטים ונחזור אליכם:</h4>
            <form class="space-y-4" @submit.prevent="handleFormSubmit">
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div class="space-y-1.5">
                  <label class="text-sm text-slate-400 font-medium px-1">שם מלא</label>
                  <input
                    v-model="formData.fullName"
                    type="text"
                    placeholder="שם העסק / שמך המלא"
                    :class="['w-full bg-slate-800 border text-white placeholder-slate-500 rounded-xl px-4 py-3 focus:outline-none focus:ring-1 transition-colors',
                             errors.fullName ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-slate-700 focus:border-pink-500 focus:ring-pink-500']" />
                  <p v-if="errors.fullName" class="text-red-400 text-xs px-1">נא להזין שם מלא</p>
                </div>
                <div class="space-y-1.5">
                  <label class="text-sm text-slate-400 font-medium px-1">מספר טלפון</label>
                  <input
                    v-model="formData.phone"
                    type="tel"
                    placeholder="050-0000000"
                    :class="['w-full bg-slate-800 border text-white placeholder-slate-500 rounded-xl px-4 py-3 focus:outline-none focus:ring-1 transition-colors',
                             errors.phone ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-slate-700 focus:border-pink-500 focus:ring-pink-500']" />
                  <p v-if="errors.phone" class="text-red-400 text-xs px-1">נא להזין מספר טלפון תקין</p>
                </div>
              </div>
              <div class="space-y-1.5">
                <label class="text-sm text-slate-400 font-medium px-1">כתובת אימייל</label>
                <input
                  v-model="formData.email"
                  type="email"
                  placeholder="mail@example.com"
                  class="w-full bg-slate-800 border border-slate-700 text-white placeholder-slate-500 rounded-xl px-4 py-3 focus:outline-none focus:border-pink-500 focus:ring-1 focus:ring-pink-500 transition-colors text-left"
                  dir="ltr" />
              </div>
              <div class="space-y-1.5">
                <label class="text-sm text-slate-400 font-medium px-1">איך נוכל לעזור?</label>
                <textarea
                  v-model="formData.message"
                  rows="4"
                  placeholder="כתבי לנו כאן כל מה שתרצו לדעת..."
                  :class="['w-full bg-slate-800 border text-white placeholder-slate-500 rounded-xl px-4 py-3 focus:outline-none focus:ring-1 transition-colors resize-none',
                           errors.message ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-slate-700 focus:border-pink-500 focus:ring-pink-500']"></textarea>
                <p v-if="errors.message" class="text-red-400 text-xs px-1">נא להזין הודעה</p>
              </div>
              <button
                type="submit"
                :disabled="isLoading"
                class="w-full bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-400 hover:to-purple-500 disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold py-4 rounded-xl flex items-center justify-center gap-2 transition-[background,opacity] shadow-lg mt-4">
                <span v-if="isLoading">שולח...</span>
                <template v-else>
                  <span>שליחת פנייה</span>
                  <Send class="w-4 h-4 -rotate-90" />
                </template>
              </button>
            </form>
          </template>

        </div>

      </div>
    </div>
  </section>
</template>

